import React, { ComponentType, useEffect, useRef } from 'react';
import { Location, NavigateOptions, useLocation, useNavigate, useParams } from 'react-router-dom';

/**
 * The project's `withRouter`, and the prop types that go with it.
 *
 * react-router 6 removed `withRouter` in favour of hooks, and every one of the 63 call sites
 * here is a class component, which cannot call hooks. Converting them is a real project —
 * each is an `@observer` class using `inject`, so it is a mobx change as much as a router one
 * — so this wraps the hooks instead and hands down the props those components already expect.
 *
 * It is a compatibility layer with an expiry date, not a destination. Delete it a component
 * at a time as they become function components.
 *
 * **What `history` is here.** v7 gives `useNavigate`, not a history object, so this assembles
 * one with the members this codebase actually uses: `push` (152 sites), `replace` (5),
 * `goBack` (2), `go` (1), `location` (48) and `listen` (8).
 *
 * The roadmap's audit said `listen` and `go` appeared **zero** times and used that to argue a
 * shim was viable at all. Both were wrong — the audit counted `history.listen` as a member of
 * the `history` package rather than searching the property. `listen` is backed here by an
 * effect on `location`, which is close enough for the one thing all eight call sites do:
 * refetch when the URL changes. It is **not** a full history subscription — it fires after
 * render rather than during navigation, and it never sees an action type.
 *
 * Still genuinely absent, and still zero in the codebase: `history.block` and `<Prompt>`.
 * Neither can be shimmed on v7, so if either appears, that component wants converting.
 *
 * `push(path, state)` is v5's two-argument signature, and it is kept: the state moves into
 * v7's options object here so that no call site changes.
 */

/**
 * v5 accepted either a path or a partial location object, and this codebase uses both —
 * `history.push('/x')` and `history.push({ pathname, search })`. v7's navigate accepts the
 * same two shapes, so both pass straight through.
 */
export type To = string | { pathname?: string; search?: string; hash?: string; state?: unknown };

/**
 * Re-exported so that nothing has to import location types from the `history` package, which
 * react-router 7 no longer depends on. v7's `Location` requires a `key`, where history's made
 * it optional, so the two are not interchangeable in an `extends` clause.
 *
 * `LocationDescriptor` was v5's "anything you can navigate to"; `To` is its equivalent here.
 */
export type { Location } from 'react-router-dom';
// eslint-disable-next-line @typescript-eslint/no-unused-vars
export type LocationDescriptor<S = unknown> = To;

export type LocationListener = (location: Location) => void;

export interface History {
  push(to: To, state?: unknown): void;
  replace(to: To, state?: unknown): void;
  goBack(): void;
  go(delta: number): void;
  /** Fires after each location change. Returns the unsubscribe, as v5's did. */
  listen(listener: LocationListener): () => void;
  location: Location;
}

/**
 * Three type parameters, because v5's had three and seven files pass more than one. Only the
 * first is used: the other two described a static router context and a location-state type
 * that v7 has no equivalent for.
 */
// eslint-disable-next-line @typescript-eslint/no-unused-vars
export interface RouteComponentProps<P = Record<string, string | undefined>, C = unknown, S = unknown> {
  history: History;
  location: Location;
  match: { params: P };
}

/**
 * v5 distinguished these by whether the component was rendered by a `<Route>` directly. v7
 * has no such distinction, so the two are the same shape; the alias exists only so the five
 * files using it keep compiling.
 */
export type RouteChildrenProps<P = Record<string, string | undefined>> = RouteComponentProps<P>;

/** Lifts a `state` out of the object form into v7's options argument. */
const toOptions = (to: To): NavigateOptions =>
  (typeof to === 'object' && to.state !== undefined ? { state: to.state } : {});

/**
 * The two type parameters mirror react-router 5's own signature. With only `P`, TypeScript
 * infers it from the class *instance* rather than its props, and every call site fails with
 * "Type '{}' is missing the following properties: state, props, componentDidMount, render".
 */
export function withRouter<P extends RouteComponentProps<unknown>, C extends ComponentType<P>>(
  Wrapped: C & ComponentType<P>
): ComponentType<Omit<P, keyof RouteComponentProps>> {
  const componentName = Wrapped.displayName || Wrapped.name || 'Component';

  const WithRouter = (props: Omit<P, keyof RouteComponentProps>) => {
    const navigate = useNavigate();
    const location = useLocation();
    const params = useParams();
    const listeners = useRef(new Set<LocationListener>());

    /**
     * `history.location` has to be live, not the location this render closed over.
     *
     * v5's history was a mutable object: reading `history.location` straight after a push gave
     * the new entry. `useLocation()` is a value frozen for the render, so a handler that pushes
     * twice — and several here do, because `QueryStringHelper.set` builds each URL from
     * `history.location.search` — computed its second URL from the first one's starting point
     * and threw the first change away. Choosing a grade on /students/my silently did nothing.
     *
     * So the ref tracks the rendered location, and push/replace update it optimistically.
     */
    const current = useRef(location);
    current.current = location;

    /**
     * v5's history.listen fired on navigation; this fires on the render that follows it. Every
     * call site uses it to refetch when the URL changes, which that ordering serves.
     *
     * The mount run is skipped deliberately. An effect runs once on mount, but v5 notified only
     * on *changes* — and all eight subscribers register in componentDidMount right after
     * fetching, so firing on mount would make every one of them fetch twice.
     */
    const subscribedAt = useRef(location);
    useEffect(
      () => {
        if (subscribedAt.current === location) {
          return;
        }

        listeners.current.forEach(listener => listener(location));
      },
      [location]
    );

    const advance = (to: To) => {
      const base = current.current;

      if (typeof to !== 'string') {
        current.current = { ...base, ...to } as Location;

        return;
      }

      const [beforeHash, hash = ''] = to.split('#');
      const [pathname, search = ''] = beforeHash.split('?');

      current.current = {
        ...base,
        pathname: pathname || base.pathname,
        search: search ? `?${search}` : '',
        hash: hash ? `#${hash}` : '',
      };
    };

    const history: History = {
      // `void` because v7's navigate returns `void | Promise<void>` and v5's history methods
      // returned nothing. Nothing here awaits a navigation, and the components calling these
      // cannot — they are the reason this shim exists.
      //
      // A `state` inside the object form wins over the second argument, matching v5.
      push: (to, state) => {
        advance(to);
        void navigate(to as string, { state, ...toOptions(to) });
      },
      replace: (to, state) => {
        advance(to);
        void navigate(to as string, { state, ...toOptions(to), replace: true });
      },
      goBack: () => { void navigate(-1); },
      go: (delta) => { void navigate(delta); },
      listen: (listener) => {
        listeners.current.add(listener);

        return () => {
          listeners.current.delete(listener);
        };
      },
      get location() {
        return current.current;
      },
    };

    // One cast, at the boundary: P's own props come from the caller and the three router
    // props are supplied here, but TypeScript cannot see that the two halves add up to P.
    const merged = { ...props, history, location, match: { params } } as unknown as P;

    return React.createElement(Wrapped, merged);
  };

  WithRouter.displayName = `withRouter(${componentName})`;

  return WithRouter;
}
